import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Loader2, Heart } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "./LanguageContext";
import { Reveal } from "./Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { IznikTexture } from "./Motifs";
import { API } from "./config";

function formatDate(iso, lang) {
  try {
    return new Date(iso).toLocaleDateString(lang === "tr" ? "tr-TR" : "nl-NL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Guestbook() {
  const { t, lang } = useLanguage();
  const g = t.guestbook;
  const [entries, setEntries] = useState([]);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/guestbook`);
      setEntries(data);
    } catch {
      /* ignore load errors */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/guestbook`, {
        name: name.trim(),
        message: message.trim(),
        lang,
      });
      setName("");
      setMessage("");
      toast.success(g.success);
      load();
    } catch {
      toast.error(g.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      data-testid="guestbook-section"
      className="relative bg-cream py-24 md:py-36 overflow-hidden"
    >
      <IznikTexture size={320} />
      <div className="relative z-10 mx-auto max-w-5xl px-6 md:px-10">
        <Reveal>
          <div className="text-center">
            <span className="font-sans text-xs uppercase tracking-[0.28em] text-pinky">
              06 — {g.kicker}
            </span>
            <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight text-ink">
              {g.title}
            </h2>
            <p className="mx-auto mt-4 max-w-md font-sans text-base text-ink/65">
              {g.desc}
            </p>
          </div>
        </Reveal>

        {/* form */}
        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            className="mx-auto mt-12 max-w-xl rounded-sm border border-gold/40 bg-white p-6 md:p-8"
          >
            <Input
              data-testid="guestbook-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={g.namePh}
              maxLength={60}
              className="border-ink/15 bg-cream focus-visible:ring-turq"
            />
            <Textarea
              data-testid="guestbook-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={g.messagePh}
              rows={3}
              maxLength={600}
              className="mt-3 border-ink/15 bg-cream focus-visible:ring-turq"
            />
            <button
              type="submit"
              data-testid="guestbook-submit"
              disabled={sending}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-turq px-7 py-3 font-sans text-xs uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-ink disabled:opacity-60"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {sending ? g.sending : g.submit}
            </button>
          </form>
        </Reveal>

        {/* entries */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {entries.length === 0 ? (
            <p className="col-span-full text-center font-sans text-sm text-ink/45">
              {g.empty}
            </p>
          ) : (
            entries.map((entry, i) => (
              <motion.div
                key={entry.id}
                data-testid="guestbook-entry"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.06 }}
                className="rounded-sm border border-gold/25 bg-white/70 p-6"
              >
                <Heart className="h-4 w-4 text-pinky" strokeWidth={1.8} />
                <p className="mt-3 font-display text-xl italic leading-snug text-ink">
                  “{entry.message}”
                </p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-sans text-sm font-medium text-turq">
                    {entry.name}
                  </span>
                  <span className="font-sans text-xs text-ink/40">
                    {formatDate(entry.created_at, lang)}
                  </span>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
